import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMessagingUnread } from '../context/MessagingUnreadContext';
import '../App.css';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WorkIcon from '@mui/icons-material/FolderSpecial';
import LocalShippingIcon from '@mui/icons-material/DeliveryDining';
import ScienceIcon from '@mui/icons-material/Biotech';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import ForumIcon from '@mui/icons-material/Forum';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import GavelIcon from '@mui/icons-material/Gavel';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Tune';

function isMessagingMenuPath(path) {
  const q = path.split('?')[1] || '';
  return new URLSearchParams(q).get('tab') === 'messaging';
}

const Sidebar = ({ user, isOpen, onToggle, onClose }) => {
  const location = useLocation();
  const { totalUnread } = useMessagingUnread();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Auto-close on mobile when resizing to mobile, auto-open on desktop
      if (mobile && isOpen) {
        // Keep current state on resize
      } else if (!mobile && !isOpen) {
        // Auto-open on desktop if it was closed
        if (onToggle) onToggle();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, onToggle]);

  // Swipe detection for mobile
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && isMobile && isOpen && onToggle) {
      onToggle(); // Close sidebar on left swipe
    }
    if (isRightSwipe && isMobile && !isOpen && onToggle) {
      onToggle(); // Open sidebar on right swipe
    }
  };

  const toggleSidebar = () => {
    if (onToggle) {
      onToggle();
    }
  };

  const isActive = (itemPath) => {
    const currentPath = location.pathname;
    const currentSearch = location.search;
    const currentParams = new URLSearchParams(currentSearch);
    
    // Check if path matches
    const itemBasePath = itemPath.split('?')[0];
    if (currentPath !== itemBasePath) return false;
    
    // Get the menu item's tab param if it exists
    const itemParams = new URLSearchParams(itemPath.split('?')[1] || '');
    const itemTab = itemParams.get('tab');
    const currentTab = currentParams.get('tab') || 'overview';
    
    // If menu item has a tab param, check if it matches
    if (itemTab) {
      return currentTab === itemTab;
    }
    
    // If no tab param in menu item, it's the base dashboard (overview)
    return !currentParams.has('tab') || currentTab === 'overview';
  };

  // Role-based menu items
  const getMenuItems = () => {
    switch (user?.role) {
      case 'admin':
        return [
          { path: '/dashboard', label: 'Overview', icon: DashboardIcon },
          { path: '/dashboard?tab=users', label: 'Users', icon: PeopleIcon },
          { path: '/dashboard?tab=wallets', label: 'Wallets', icon: AccountBalanceWalletIcon },
          { path: '/dashboard?tab=reports', label: 'Reports', icon: AssessmentIcon },
          { path: '/dashboard?tab=subscription-plans', label: 'Subscription Plans', icon: CreditCardIcon },
          { path: '/dashboard?tab=bills', label: 'Bills', icon: AssessmentIcon },
          { path: '/dashboard?tab=staff', label: 'Staff', icon: PeopleIcon },
          { path: '/dashboard?tab=analytics', label: 'Analytics', icon: BarChartIcon },
          { path: '/dashboard?tab=logs', label: 'Audit logs', icon: HistoryIcon },
          { path: '/dashboard?tab=disputes', label: 'Disputes', icon: GavelIcon },
          { path: '/dashboard?tab=demographics-options', label: 'Demographics options', icon: AssessmentIcon },
          { path: '/dashboard?tab=settings', label: 'Account', icon: SettingsIcon }
        ];
      case 'lab':
        return [
          { path: '/dashboard', label: 'Overview', icon: DashboardIcon },
          { path: '/dashboard?tab=jobs', label: 'Jobs', icon: WorkIcon },
          { path: '/dashboard?tab=pickup-requests', label: 'Pickup Requests', icon: LocalShippingIcon },
          { path: '/dashboard?tab=service-requests', label: 'Clinic requests', icon: AssignmentIcon },
          { path: '/dashboard?tab=demographics', label: 'Demographics', icon: AssessmentIcon },
          { path: '/dashboard?tab=audit-logs', label: 'Audit log', icon: HistoryIcon },
          { path: '/dashboard?tab=reports', label: 'Reports', icon: AssessmentIcon },
          { path: '/dashboard?tab=reagents', label: 'Reagent Requests', icon: ScienceIcon },
          { path: '/dashboard?tab=bills', label: 'Bills', icon: AssessmentIcon },
          { path: '/dashboard?tab=clinics', label: 'Clinics', icon: BusinessCenterIcon },
          { path: '/dashboard?tab=analytics', label: 'Analytics', icon: BarChartIcon },
          { path: '/dashboard?tab=employees', label: 'Employees', icon: PeopleIcon },
          { path: '/dashboard?tab=community', label: 'Community & News', icon: ForumIcon },
          { path: '/dashboard?tab=messaging', label: 'Messages', icon: ChatIcon },
          { path: '/dashboard?tab=settings', label: 'Settings', icon: BarChartIcon }
        ];
      case 'clinic':
        return [
          { path: '/dashboard', label: 'Overview', icon: DashboardIcon },
          { path: '/dashboard?tab=pickup-requests', label: 'Pickup Requests', icon: LocalShippingIcon },
          { path: '/dashboard?tab=service-requests', label: 'Lab requests', icon: AssignmentIcon },
          { path: '/dashboard?tab=demographics', label: 'Demographics', icon: AssessmentIcon },
          { path: '/dashboard?tab=audit-logs', label: 'Audit log', icon: HistoryIcon },
          { path: '/dashboard?tab=reports', label: 'Reports', icon: AssessmentIcon },
          { path: '/dashboard?tab=analytics', label: 'Analytics', icon: BarChartIcon },
          { path: '/dashboard?tab=messaging', label: 'Messages', icon: ChatIcon },
          { path: '/dashboard?tab=settings', label: 'Account', icon: SettingsIcon }
        ];
      case 'phlebotomist':
        return [
          { path: '/dashboard', label: 'Overview', icon: DashboardIcon },
          { path: '/dashboard?tab=available-jobs', label: 'Available Jobs', icon: AssignmentIcon },
          { path: '/dashboard?tab=my-jobs', label: 'My Jobs', icon: WorkIcon },
          { path: '/dashboard?tab=wallet', label: 'Wallet', icon: AccountBalanceWalletIcon },
          { path: '/dashboard?tab=bills', label: 'My Bills', icon: AssessmentIcon },
          { path: '/dashboard?tab=analytics', label: 'Analytics', icon: BarChartIcon },
          { path: '/dashboard?tab=messaging', label: 'Messages', icon: ChatIcon },
          { path: '/dashboard?tab=settings', label: 'Account', icon: SettingsIcon }
        ];
      case 'supplier':
        return [
          { path: '/dashboard', label: 'Overview', icon: DashboardIcon },
          { path: '/dashboard?tab=requests', label: 'Available Requests', icon: AssignmentIcon },
          { path: '/dashboard?tab=my-offers', label: 'My Offers', icon: BusinessCenterIcon },
          { path: '/dashboard?tab=supply-history', label: 'Supply History', icon: HistoryIcon },
          { path: '/dashboard?tab=bills', label: 'My Bills', icon: AssessmentIcon },
          { path: '/dashboard?tab=analytics', label: 'Analytics', icon: BarChartIcon },
          { path: '/dashboard?tab=messaging', label: 'Messages', icon: ChatIcon },
          { path: '/dashboard?tab=settings', label: 'Account', icon: SettingsIcon }
        ];
      case 'lab_employee':
        return [
          { path: '/dashboard', label: 'Overview', icon: DashboardIcon },
          { path: '/dashboard?tab=calendar', label: 'Calendar', icon: EventIcon },
          { path: '/dashboard?tab=my-tasks', label: 'My Tasks', icon: AssignmentIcon },
          { path: '/dashboard?tab=analytics', label: 'Analytics', icon: BarChartIcon },
          { path: '/dashboard?tab=messaging', label: 'Messages', icon: ChatIcon },
          { path: '/dashboard?tab=profile', label: 'Profile', icon: PeopleIcon }
        ];
      default:
        return [];
    }
  };

  const menuItems = getMenuItems();

  return (
    <>
      {/* Mobile overlay with swipe support - keep as-is (locked behavior) */}
      {isOpen && isMobile && (
        <div 
          className="sidebar-overlay" 
          onClick={toggleSidebar}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}


      {/* Sidebar */}
      <aside 
        className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          position: 'fixed',
          left: isOpen ? 0 : (isMobile ? '-280px' : '0'),
          top: '0',
          height: '100vh',
          width: isOpen ? (isMobile ? '280px' : '256px') : (isMobile ? '0px' : '64px'),
          maxWidth: isMobile ? (isOpen ? '85vw' : '0px') : (isOpen ? '256px' : '64px'),
          background: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border-color)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isOpen 
            ? (isMobile 
                ? '4px 0 20px rgba(0, 0, 0, 0.15), 2px 0 8px rgba(0, 0, 0, 0.1)' 
                : '4px 0 24px rgba(0, 0, 0, 0.12), 2px 0 12px rgba(0, 0, 0, 0.08)')
            : '2px 0 8px rgba(0, 0, 0, 0.05)',
          backdropFilter: isMobile ? 'blur(10px)' : 'none'
        }}
      >
        {/* Logo and Toggle Button - Only visible when sidebar is open */}
        {isOpen && (
          <div style={{
            padding: '20px',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flex: 1 }}>
              <img 
                src="/logo-labster-banner.jpeg" 
                alt="THE LABSTER" 
                style={{ 
                  height: '40px', 
                  width: 'auto',
                  maxWidth: '100%'
                }} 
              />
            </Link>
            <button
              onClick={toggleSidebar}
              className="sidebar-toggle-btn"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '6px',
                boxShadow: '0 0 12px rgba(51, 255, 217, 0.5), 0 0 24px rgba(181, 255, 51, 0.3), inset 0 0 8px rgba(51, 255, 217, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: 'auto',
                position: 'relative',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(51, 255, 217, 0.7), 0 0 40px rgba(181, 255, 51, 0.5), inset 0 0 12px rgba(51, 255, 217, 0.3)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 12px rgba(51, 255, 217, 0.5), 0 0 24px rgba(181, 255, 51, 0.3), inset 0 0 8px rgba(51, 255, 217, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              aria-label="Close sidebar"
            >
              <span style={{
                width: '18px',
                height: '2px',
                background: 'var(--logo-cyan-teal)',
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(51, 255, 217, 0.8), 0 0 8px rgba(181, 255, 51, 0.5)',
                transition: 'all 0.3s ease'
              }}></span>
              <span style={{
                width: '18px',
                height: '2px',
                background: 'var(--logo-cyan-teal)',
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(51, 255, 217, 0.8), 0 0 8px rgba(181, 255, 51, 0.5)',
                transition: 'all 0.3s ease'
              }}></span>
              <span style={{
                width: '18px',
                height: '2px',
                background: 'var(--logo-cyan-teal)',
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(51, 255, 217, 0.8), 0 0 8px rgba(181, 255, 51, 0.5)',
                transition: 'all 0.3s ease'
              }}></span>
            </button>
          </div>
        )}
        
        {/* Toggle Button when sidebar is closed – desktop: inside 64px strip; mobile: strip hidden, Navbar shows toggle */}
        {!isOpen && (
          <div style={{
            padding: '20px',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80px',
            height: '80px'
          }}>
            <button
              onClick={toggleSidebar}
              className="sidebar-toggle-btn"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '6px',
                boxShadow: '0 0 12px rgba(51, 255, 217, 0.5), 0 0 24px rgba(181, 255, 51, 0.3), inset 0 0 8px rgba(51, 255, 217, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: 'auto',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(51, 255, 217, 0.7), 0 0 40px rgba(181, 255, 51, 0.5), inset 0 0 12px rgba(51, 255, 217, 0.3)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 12px rgba(51, 255, 217, 0.5), 0 0 24px rgba(181, 255, 51, 0.3), inset 0 0 8px rgba(51, 255, 217, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              aria-label="Open sidebar"
            >
              <span style={{
                width: '18px',
                height: '2px',
                background: 'var(--logo-cyan-teal)',
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(51, 255, 217, 0.8), 0 0 8px rgba(181, 255, 51, 0.5)',
                transition: 'all 0.3s ease'
              }}></span>
              <span style={{
                width: '18px',
                height: '2px',
                background: 'var(--logo-cyan-teal)',
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(51, 255, 217, 0.8), 0 0 8px rgba(181, 255, 51, 0.5)',
                transition: 'all 0.3s ease'
              }}></span>
              <span style={{
                width: '18px',
                height: '2px',
                background: 'var(--logo-cyan-teal)',
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(51, 255, 217, 0.8), 0 0 8px rgba(181, 255, 51, 0.5)',
                transition: 'all 0.3s ease'
              }}></span>
            </button>
          </div>
        )}
        
        {/* Menu Items */}
        <nav style={{
          flex: '1 1 auto',
          padding: isOpen ? '16px' : '12px 0',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: isOpen ? 'stretch' : 'center',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          gap: isOpen ? '8px' : '12px'
        }}>
          {menuItems.map((item, index) => {
            const active = isActive(item.path);
            const msgUnread = isMessagingMenuPath(item.path) && totalUnread > 0;
            return (
              <Link
                key={index}
                  to={item.path}
                  onClick={() => {
                    const params = new URLSearchParams(item.path.split('?')[1] || '');
                    if (params.has('tab')) {
                      window.dispatchEvent(new CustomEvent('tabChange', { detail: params.get('tab') }));
                    } else {
                      window.dispatchEvent(new CustomEvent('tabChange', { detail: 'overview' }));
                    }
                    if (isMobile && onClose) onClose();
                  }}
                  title={!isOpen ? item.label : ''}
                  className={msgUnread ? 'sidebar-nav-link sidebar-nav-link--message-unread' : 'sidebar-nav-link'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isOpen ? 'flex-start' : 'center',
                    padding: isOpen ? '14px 20px' : '14px',
                    color: active ? 'var(--logo-cyan-teal)' : 'rgba(51, 255, 217, 0.85)',
                    textDecoration: 'none',
                    background: active 
                      ? (isOpen 
                          ? 'rgba(51, 255, 217, 0.08)' 
                          : 'rgba(51, 255, 217, 0.1)')
                      : msgUnread
                        ? (isOpen ? 'rgba(244, 63, 94, 0.08)' : 'rgba(244, 63, 94, 0.12)')
                        : 'transparent',
                    transition: 'all 0.2s ease',
                    borderRadius: isOpen ? '8px' : '10px',
                    fontWeight: active ? '600' : '400',
                    fontSize: isOpen ? '0.875rem' : '0',
                    minWidth: isOpen ? 'auto' : '56px',
                    width: isOpen ? 'auto' : '56px',
                    height: isOpen ? '44px' : '52px',
                    flexShrink: 0,
                    borderLeft: isOpen && active ? '2px solid var(--logo-cyan-teal)' : (isOpen && msgUnread ? '3px solid rgb(244, 63, 94)' : 'none'),
                    gap: isOpen ? '14px' : '0',
                    position: 'relative',
                    boxShadow: msgUnread && !active ? 'inset 0 0 0 1px rgba(244, 63, 94, 0.25)' : undefined
                  }}
                >
                  {item.icon && (
                    <span style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isOpen ? '1.25rem' : '1.5rem',
                      minWidth: isOpen ? '24px' : 'auto',
                      width: isOpen ? '24px' : '100%',
                      color: active ? 'var(--logo-cyan-teal)' : 'rgba(51, 255, 217, 0.85)',
                      position: 'relative'
                    }}>
                      {React.createElement(item.icon, { 
                        style: { 
                          fontSize: isOpen ? '1.25rem' : '1.5rem',
                          color: 'inherit'
                        }
                      })}
                      {msgUnread && !isOpen && (
                        <span className="sidebar-message-dot" aria-hidden />
                      )}
                    </span>
                  )}
                  {isOpen && (
                    <span style={{ 
                      fontWeight: active ? '600' : '400',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      textAlign: 'left'
                    }}>
                      {item.label}
                    </span>
                  )}
                  {msgUnread && isOpen && (
                    <span className="sidebar-message-badge" aria-label={`${totalUnread} unread messages`}>
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                </Link>
            );
          })}
        </nav>

        {/* User Info Footer */}
        <div style={{
          padding: isOpen ? '20px' : '20px 12px',
          borderTop: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
          display: 'flex',
          flexDirection: isOpen ? 'column' : 'column',
          alignItems: isOpen ? 'flex-start' : 'center',
          justifyContent: 'center',
          minHeight: isOpen ? 'auto' : '88px',
          flexShrink: 0,
          width: '100%'
        }}>
          {isOpen ? (
            <>
              <div style={{
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '4px',
                textAlign: 'left',
                width: '100%',
                overflow: 'visible',
          overflowY: 'hidden',
          overflowX: 'visible',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.name}
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textAlign: 'left',
                width: '100%'
              }}>
                {user?.role}
              </div>
            </>
          ) : (
            <div style={{
              color: 'var(--logo-cyan-teal)',
              fontSize: '1.75rem',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              width: '100%',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textShadow: '0 0 8px rgba(51, 255, 217, 0.5)',
              padding: '8px',
              borderRadius: '8px',
              background: 'rgba(51, 255, 217, 0.1)',
              border: '1px solid rgba(51, 255, 217, 0.2)'
            }}>
              {user?.role?.charAt(0) || 'U'}
            </div>
          )}
        </div>
      </aside>

    </>
  );
};

export default Sidebar;

